import { Component, inject } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { IonicModule } from '@ionic/angular';
import { AuthService } from 'src/app/oidc/auth.service';
import { LOGGER } from 'src/app/oidc/shared/logger';
import { WIZARD_STEP_HOST } from '../wizard-step-host';

const SECOND_FACTOR_HELP_URL = 'https://service.tu-dortmund.de/mfa-sso';

/**
 * Shown only to people coming from a version that still had the old oauth2 login — 
 * never on a fresh install, where there is no change to announce. See `onFreshInstall`
 * in the catalogue.
 */
@Component({
  selector: 'app-wizard-login-oidc',
  standalone: true,
  imports: [IonicModule],
  template: `
    <section class="intro ion-text-center">
      <div class="icon-wrap">
        <ion-icon name="shield-checkmark-outline"></ion-icon>
      </div>
      <h2>Einmal neu anmelden</h2>
      <p>
        Die App verwendet jetzt ein neues Anmeldeverfahren. Deshalb wurdest du einmalig
        abgemeldet.
      </p>
    </section>

    <div class="login-info">
      <ion-icon name="person-circle-outline"></ion-icon>
      <p>
        Melde dich erneut mit deinem UniAccount an. Dafür ist eine
        Zwei-Faktor-Authentifizierung erforderlich.
      </p>
    </div>

    @if (!authService.isAuthenticated()) {
      <ion-button expand="block" (click)="loginNow()">
        <ion-icon slot="start" name="log-in-outline"></ion-icon>
        Erneut anmelden
      </ion-button>
    }

    <ion-button class="help-button" expand="block" fill="clear" (click)="openHelp()">
      Hilfe zur Zwei-Faktor-Authentifizierung
      <ion-icon slot="end" name="open-outline"></ion-icon>
    </ion-button>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 520px;
      margin: 24px auto 0;
    }

    .intro {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .icon-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 104px;
      height: 104px;
      margin-bottom: 8px;
      border-radius: 32px;
      color: var(--ion-color-primary);
      background: rgba(var(--ion-color-primary-rgb), 0.12);
    }

    .icon-wrap ion-icon {
      font-size: 58px;
    }

    h2 {
      margin: 12px 0 8px;
      font-size: 1.55rem;
      font-weight: 700;
    }

    .intro p {
      max-width: 440px;
      margin: 0;
      color: var(--ion-color-step-600, #666);
      line-height: 1.5;
    }

    .login-info {
      display: flex;
      gap: 14px;
      align-items: center;
      padding: 16px;
      border: 1px solid rgba(var(--ion-color-primary-rgb), 0.25);
      border-radius: 16px;
      background: rgba(var(--ion-color-primary-rgb), 0.06);
    }

    .login-info ion-icon {
      flex: 0 0 auto;
      color: var(--ion-color-primary);
      font-size: 32px;
    }

    .login-info p {
      margin: 0;
      line-height: 1.45;
    }

    ion-button:not(.help-button) {
      margin-top: 4px;
      min-height: 48px;
    }

    .help-button {
      margin-top: -8px;
      font-size: 0.9rem;
    }

    @media (max-width: 380px) {
      .icon-wrap {
        width: 88px;
        height: 88px;
      }

      .icon-wrap ion-icon {
        font-size: 50px;
      }
    }
  `
})
export class LoginOIDCStepComponent {
  readonly authService = inject(AuthService);
  private readonly host = inject(WIZARD_STEP_HOST);
  private readonly logger = inject(LOGGER).scope('wizard');

  openHelp(): void {
    void Browser.open({ url: SECOND_FACTOR_HELP_URL }).catch((e: unknown) =>
      this.logger.warn('could not open the help page', { error: String(e) }),
    );
  }

  /**
   * The step is completed and flushed BEFORE the login starts. `login()` sends the app
   * into the browser and, in redirect mode, reloads it on the way back — nothing after
   * this line is guaranteed to run, so anything left until then would leave the notice
   * queued and pop it up again on top of a freshly logged-in app.
   */
  async loginNow(): Promise<void> {
    await this.host.handOver();
    await this.authService.login().catch((e: unknown) =>
      this.logger.error('login could not be started', { error: String(e) }),
    );
  }

  async logoutNow() {
    this.authService.logout(async () => {});
  }
}
